/**
 * When may a decision be made about a candidate?
 *
 * The asymmetry IS the feature: **you need proof to advance someone, and none to pass on them.**
 *
 *   Shortlist  — Screening tab only, and only once the call has finished. "Yes" demands evidence.
 *   Reject     — anywhere, anytime, on anyone.
 *   Backlog    — anywhere, anytime, on anyone.
 *   Undo       — anywhere, on anyone already decided.
 *
 * Making reject/backlog require a finished call was considered and rejected: a paused candidate
 * isn't finished (that's what lets them be resumed), so they could never be rejected — stranding
 * them on the Screening tab forever, which is the graveyard the Rejected bucket exists to drain.
 * And on a 500-résumé job where 10 were screened, the other 490 could never be decided at all.
 *
 * Kept out of SelectionBar because the rules are worth testing on their own: the component only
 * needs to know whether to disable a button and what to say, not how the answer was reached.
 */

import { isScreened } from "./screeningStatus";
import { DECISION } from "./candidateRow";

const names = (rows) => rows.map((r) => r.name || r.resumeId).join(", ");

/**
 * Splits the selected rows into the two ways SHORTLISTING can be blocked. Reject and backlog have
 * no guards, so they need none of this.
 *
 * `screeningActive` fails CLOSED: screeningByResumeId is populated by an async poll, so a candidate
 * whose status hasn't landed yet reads as not-final (screenProgress of undefined is "not
 * scheduled") and counts as active. A momentarily disabled button beats shortlisting someone whose
 * call is still ringing.
 */
export const shortlistGuards = (picked, screeningByResumeId = {}) => ({
  notScreened: picked.filter((r) => !r.screeningScheduled),
  screeningActive: picked.filter(
    (r) => r.screeningScheduled && !isScreened(screeningByResumeId[r.resumeId])
  ),
});

/**
 * "" means allowed. Anything else is both the block and the tooltip explaining it.
 *
 * Tab first: on any other tab the button is disabled no matter who is selected, so leading with a
 * per-candidate complaint ("Priya's call is still running") would send the user off to fix the
 * wrong thing — the answer is "you're on the wrong tab", whatever the selection looks like.
 */
export const shortlistReason = ({ stage, notScreened, screeningActive }) => {
  if (stage !== "screening") {
    return "Shortlist from the Screening tab — candidates must be screened first.";
  }
  if (screeningActive.length) {
    return `Screening in progress — ${names(screeningActive)}. Wait for the call to finish.`;
  }
  // Unreachable from the Screening tab, which only lists scheduled candidates — but the tab is a
  // filter, not an invariant, and a stale selection can outlive a refresh. Fail closed.
  if (notScreened.length) {
    return `Not screened yet — ${names(notScreened)}.`;
  }
  return "";
};

/**
 * Reject and backlog are NEVER blocked — no tab rule, no call rule, no evidence required. These
 * exist so the call sites read the same as the shortlist one, and so that if the rule ever does
 * grow a condition, there is one place for it to grow.
 */
export const rejectReason = () => "";
export const backlogReason = () => "";

/**
 * The decision every selected candidate shares, or null if they differ or are undecided. Drives the
 * "Undo decision" button, which is simply absent unless there is one decision to undo — no guard
 * needed, because a button that isn't there can't be clicked.
 */
export const sharedDecision = (picked) => {
  if (!picked.length) return null;
  const [first] = picked;
  if (first.decision === DECISION.UNDECIDED) return null;
  return picked.every((r) => r.decision === first.decision) ? first.decision : null;
};
